import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Card, CardHeader, CardBody, CardTitle,
  Row, Col, Table, Button, FormGroup, Label, Input
} from "reactstrap";

import { API_BASE_URL } from "../config/api";

const BASE_URL = API_BASE_URL;
const EMPTY_HALL = { hall: "", hall_ar: "" };

function GameHallOrder() {
  const [levels, setLevels] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState("");
  const [halls, setHalls] = useState([]); // [{hall, hall_ar, sort_order}]
  const [newHall, setNewHall] = useState(EMPTY_HALL);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const notify = (setter, msg, t = 3500) => {
    setter(msg);
    setTimeout(() => setter(""), t);
  };

  useEffect(() => {
    const fetchLevels = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/game/levels?all=1`, {
          headers: { "Accept-Language": "en" }
        });
        setLevels(res.data || []);
      } catch {
        notify(setError, "Error fetching levels.");
      }
    };

    fetchLevels();
  }, []);

  const resequenceHalls = (list = []) =>
    [...list]
      .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0))
      .map((hallItem, index) => ({
        ...hallItem,
        sort_order: index + 1,
      }));

  const fetchHalls = async (level) => {
    if (!level) {
      setHalls([]);
      setNewHall(EMPTY_HALL);
      return;
    }
    try {
      const res = await axios.get(`${BASE_URL}/game/halls/order/${encodeURIComponent(level)}`);
      const raw = res.data?.data || [];
      const list = raw.map((h, idx) => ({
        hall: h.hall,
        hall_ar: h.hall_ar || "",
        sort_order: Number.isFinite(h.sort_order) ? h.sort_order : (idx + 1),
        originalHall: h.hall
      }));
      setHalls(resequenceHalls(list));
      setNewHall(EMPTY_HALL);
    } catch {
      notify(setError, "Error fetching halls.");
    }
  };

  const updateHall = (idx, patch) => {
    setHalls(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const persistHalls = async (level, hallList, successMessage = "Halls saved.") => {
    const orderedHalls = resequenceHalls(
      hallList
        .map(h => ({
          hall: String(h.hall || "").trim(),
          hall_ar: String(h.hall_ar || "").trim() || null,
          sort_order: Number(h.sort_order),
          originalHall: h.originalHall || h.hall
        }))
        .filter(h => h.hall)
    );

    const hasDuplicates = orderedHalls.some((hallItem, index) => (
      orderedHalls.findIndex(
        ({ hall }) => String(hall || "").toLowerCase() === String(hallItem.hall || "").toLowerCase()
      ) !== index
    ));

    if (hasDuplicates) {
      notify(setError, "Each hall name must be unique within the selected level.");
      return false;
    }

    await axios.put(`${BASE_URL}/game/halls/order/${encodeURIComponent(level)}`, { orderedHalls });
    notify(setSuccess, successMessage);
    await fetchHalls(level);
    return true;
  };

  const addHall = () => {
    if (!selectedLevel) {
      notify(setError, "Select a level first.");
      return;
    }

    const hall = String(newHall.hall || "").trim();
    const hall_ar = String(newHall.hall_ar || "").trim();

    if (!hall) {
      notify(setError, "Hall name (EN) is required.");
      return;
    }

    const alreadyExists = halls.some(
      (item) => String(item.hall || "").trim().toLowerCase() === hall.toLowerCase()
    );

    if (alreadyExists) {
      notify(setError, "This hall already exists for the selected level.");
      return;
    }

    const nextHalls = resequenceHalls([
      ...halls,
      {
        hall,
        hall_ar,
        sort_order: halls.length + 1,
        originalHall: null,
      },
    ]);

    setHalls(nextHalls);
    setNewHall(EMPTY_HALL);
    persistHalls(selectedLevel, nextHalls, "Hall added and saved.").catch(() => {
      notify(setError, "Failed to save hall.");
    });
  };

  const removeHall = (idx) => {
    const nextHalls = resequenceHalls(halls.filter((_, index) => index !== idx));
    setHalls(nextHalls);
    persistHalls(selectedLevel, nextHalls, "Hall deleted and saved.").catch(() => {
      notify(setError, "Failed to delete hall.");
    });
  };

  const saveOrder = async () => {
    if (!selectedLevel) return;
    try {
      await persistHalls(selectedLevel, halls, "Halls saved.");
    } catch {
      notify(setError, "Failed to save halls.");
    }
  };

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">Game Hall Order</CardTitle>
            </CardHeader>
            <CardBody>
              {error && <div className="alert alert-danger">{error}</div>}
              {success && <div className="alert alert-success">{success}</div>}

              <Row form>
                <Col md="4">
                  <FormGroup>
                    <Label>Level</Label>
                    <Input
                      type="select"
                      value={selectedLevel}
                      onChange={(e) => {
                        const lv = e.target.value;
                        setSelectedLevel(lv);
                        fetchHalls(lv);
                      }}
                    >
                      <option value="">Select level</option>
                      {levels.map(l => (
                        <option key={l.id} value={l.name}>{l.label || l.name}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
              </Row>

              <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: 12 }}>
                <h5>Level Halls</h5>
                <Button color="info" onClick={saveOrder} disabled={!selectedLevel}>Save Halls</Button>
              </div>

              <Card style={{ marginBottom: 16 }}>
                <CardBody>
                  <h6 style={{ marginBottom: 16 }}>Add Hall</h6>
                  <Row form>
                    <Col md="5">
                      <FormGroup>
                        <Label>Hall (EN)</Label>
                        <Input
                          value={newHall.hall}
                          onChange={(e) => setNewHall((prev) => ({ ...prev, hall: e.target.value }))}
                          disabled={!selectedLevel}
                          placeholder={selectedLevel ? "Enter hall name" : "Select a level first"}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="5">
                      <FormGroup>
                        <Label>Hall (AR)</Label>
                        <Input
                          value={newHall.hall_ar}
                          onChange={(e) => setNewHall((prev) => ({ ...prev, hall_ar: e.target.value }))}
                          disabled={!selectedLevel}
                          placeholder={selectedLevel ? "Arabic name" : "Select a level first"}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="2" className="d-flex align-items-end">
                      <Button color="primary" type="button" block onClick={addHall} disabled={!selectedLevel}>
                        Add Hall
                      </Button>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              <Table responsive>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Order</th>
                    <th>Hall (EN)</th>
                    <th>Hall (AR)</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {halls.map((h, idx) => (
                    <tr key={`${h.hall}-${idx}`}>
                      <td>{idx + 1}</td>
                      <td>
                        <Input
                          type="number"
                          value={h.sort_order ?? ""}
                          onChange={(e) => updateHall(idx, { sort_order: Number(e.target.value) })}
                          style={{ width: 90 }}
                        />
                      </td>
                      <td>
                        <Input
                          value={h.hall}
                          onChange={(e) => updateHall(idx, { hall: e.target.value })}
                        />
                      </td>
                      <td>
                        <Input
                          value={h.hall_ar || ""}
                          onChange={(e) => updateHall(idx, { hall_ar: e.target.value })}
                        />
                      </td>
                      <td>
                        <Button color="danger" size="sm" type="button" onClick={() => removeHall(idx)}>
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {halls.length === 0 && (
                    <tr><td colSpan="5">No halls added for this level yet.</td></tr>
                  )}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default GameHallOrder;
